import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';

interface Props {
  children: ReactNode;
  /** Optional custom fallback. Falls back to `DefaultFallback` if omitted. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
}
interface State {
  error: Error | null;
}

/**
 * Global error boundary — catches render / lifecycle / hook errors that
 * would otherwise unmount the whole SPA. Structured error logs go to the
 * browser console; anything more sensitive (API errors, network errors)
 * is already handled by TanStack Query's error state on individual queries.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error(
      JSON.stringify({
        level: 'error',
        source: 'react',
        name: error.name,
        message: error.message,
        stack: (info.componentStack ?? '').split('\n').slice(0, 6).join('\n'),
      }),
    );
  }

  reset = () => this.setState({ error: null });

  render() {
    if (this.state.error) {
      if (this.props.fallback) return this.props.fallback(this.state.error, this.reset);
      return <DefaultFallback error={this.state.error} reset={this.reset} />;
    }
    return this.props.children;
  }
}

function DefaultFallback({ error, reset }: { error: Error; reset: () => void }) {
  return (
    <div
      className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background p-8 text-center"
      role="alert"
    >
      <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
        Something went wrong
      </p>
      <h1 className="font-display text-3xl">We hit a snag rendering that page.</h1>
      <p className="max-w-md text-sm text-muted-foreground">
        Try again in a moment. If the issue persists, refresh the page or contact support.
      </p>
      <details className="mt-2 max-w-lg rounded border border-border bg-muted/30 p-3 text-left text-xs">
        <summary className="cursor-pointer font-medium">Technical detail</summary>
        <pre className="mt-2 overflow-auto text-[10px] text-muted-foreground">
          {error.name}: {error.message}
        </pre>
      </details>
      <div className="mt-4 flex gap-2">
        <Button variant="outline" onClick={() => window.location.reload()}>
          Reload page
        </Button>
        <Button onClick={reset}>Try again</Button>
      </div>
    </div>
  );
}
